// @material-ui/lab から TabContext をインポート
import { TabContext } from '@material-ui/lab';
// @material-ui/lab から TabList をインポート
import { TabList } from '@material-ui/lab';
// @material-ui/lab から TabPanel をインポート
import { TabPanel } from '@material-ui/lab';
// @mui/material から Paper をインポート
import { Paper } from '@mui/material';
// @mui/material から Tab をインポート
import { Tab } from '@mui/material';
// React から SyntheticEvent 型をインポート
import React from 'react';
// React から useState フックをインポート
import { SyntheticEvent, useEffect, useState } from 'react';
// react-i18next から useTranslation フックをインポート
import { useTranslation } from 'react-i18next';
// react-router-dom から useLocation フックをインポート
import { useLocation } from 'react-router-dom';
// 言語設定フックをインポート
import useLocales from 'src/hooks/useLocales';
// Redux ストアセレクターをインポート
import { useAppDispatch, useAppSelector } from 'src/redux/store';
// タブスタイルユーティリティをインポート
import { tabStyles } from 'src/utils/tabStyles';
// 回答確認状況タブコンポーネントをインポート
import { EnqueteConfirmTab } from '../../shared-tabs/answer-confirm-status/components/EnqueteConfirmTab';
// 回答者変更コンポーネントをインポート
import ChangeRespondentComponent from '../../shared-tabs/change-respondent/components/ChangeRespondentComponent';
// 調査期間外表示コンポーネントをインポート
import OutsideSurveyPeriodTypography from '../../shared-tabs/outside-survey/components/OutsideSurveyPeriodTypography';
// 一般質問フォームタブコンポーネントをインポート
import EnqueteAnswerGeneralQuestionCompliaFormTab from './EnqueteAnswerSpecialQuestionCompliaFormTab';
// 一般質問(アクション⑤)フォームタブコンポーネントをインポート
import EnqueteAnswerGeneralQuestionCompliaFormAction5Tab from './EnqueteAnswerSpecialQuestionCompliaFormAction5Tab';
// スタイル付きボタンコンポーネントをインポート
import { setCurrentAnswerTab } from 'src/features/general/enquete-answer/store/enqueteAnswerSlice';
//一問一答(コンプラ)タブの値を定義する定数オブジェクト取得
import { TAB_VALUE_COMPLIA as TAB_VALUE } from 'src/constants';

// TabValue 型を定数から導出
type TabValue = typeof TAB_VALUE[keyof typeof TAB_VALUE];

// EnqueteAnswerTypeSpecialQuestionCompliaTabContainer コンポーネントを定義
export const EnqueteAnswerTypeSpecialQuestionCompliaTabContainer: React.FC = () => {
  // Redux から回答者ターゲットデータを取得
  const respondentTargetData = useAppSelector((state) => state.enqueteAnswer.respondentTargetData);
  // Redux から回答者リクエストデータを取得
  const respondentRequestData = useAppSelector((state) => state.enqueteAnswer.respondentRequestData);
  // Redux から依頼期間前かどうかを取得
  const isBeforeRequestTerm = useAppSelector((state) => state.enqueteAnswer.isBeforeRequestTerm);
  // Redux から依頼期間後かどうかを取得
  const isAfterRequestTerm = useAppSelector((state) => state.enqueteAnswer.isAfterRequestTerm);
  // Redux から現在のタブかどうかを取得
  const currentAnswerTab = useAppSelector((state) => state.enqueteAnswer.currentAnswerTab);

  // 翻訳機能を使用するためのフックを呼び出し
  const { t } = useTranslation();
  // 現在の言語設定を取得
  const { currentLang } = useLocales();

  const dispatch = useAppDispatch();

  // タブ切り替えハンドラー関数を定義
  const handleChange = (event: SyntheticEvent, newValue: TabValue) => {
    // タブ値を更新
    setTabValue(newValue);
    dispatch(setCurrentAnswerTab(newValue));

  };

  // 現在の URL location を取得
  const params = useLocation().search;
  // URLSearchParams を使用して tab パラメータを取得
  const tabParmsValue = new URLSearchParams(params).get('tab');
  // パラメータがあれば使用、なければデフォルト値を設定
  const defaultTab = !!currentAnswerTab ? currentAnswerTab : tabParmsValue ? tabParmsValue : TAB_VALUE.QUESTION;
  // タブ値の状態フックを初期化
  const [tabValue, setTabValue] = useState<TabValue>(defaultTab as TabValue);

  useEffect(() => {
    //現在の提出ポップアップタブセット
    dispatch(setCurrentAnswerTab(defaultTab));
  }, []);

  // JSX を返す
  return (
    // タブコンテキストプロバイダーで囲む
    <TabContext value={tabValue}>
      {/* // ペーパーコンポーネントで外観を定義 */}
      <Paper variant="elevation" sx={{ mb: 10 }}>
        {/* // タブリストコンポーネントを配置 */}
        <TabList onChange={handleChange}>
          {/* // 質問タブを定義 */}
          <Tab
            // 言語に応じたスタイルを適用
            style={tabStyles(currentLang.value)}
            // タブラベルを設定
            label={t('enqueteAnswerInput.tab.surveyTypeGeneralQuestion')}
            // タブ値を設定
            value={TAB_VALUE.QUESTION}
            // 左 Padding を設定
            sx={{ paddingLeft: 2 }}
            // 依頼期間前の場合は無効化
            disabled={isBeforeRequestTerm}
          />
          {/* // 質問(アクション⑤)タブを定義 */}
          <Tab
            // 言語に応じたスタイルを適用
            style={tabStyles(currentLang.value)}
            // タブラベルを設定
            label={t('enqueteAnswerInput.tab.surveyTypeGeneralQuestionAction5')}
            // タブ値を設定
            value={TAB_VALUE.QUESTION_ACTION5}
            // 左 Padding を設定
            sx={{ paddingLeft: 2 }}
            // 依頼期間前の場合は無効化
            disabled={isBeforeRequestTerm}
          />
          {/* // 回答確認状況タブを定義 */}
          <Tab
            // 言語に応じたスタイルを適用
            style={tabStyles(currentLang.value)}
            // タブラベルを設定
            label={t('enqueteAnswerInput.tab.responseConfirmationStatus')}
            // タブ値を設定
            value={TAB_VALUE.CONFIRM}
            // 依頼期間前の場合は無効化
            disabled={isBeforeRequestTerm}
          />
          {/* // 回答者変更タブを定義 */}
          <Tab
            // 言語に応じたスタイルを適用
            style={tabStyles(currentLang.value)}
            // タブラベルを設定
            label={t('enqueteAnswerInput.tab.changeRespondents')}
            // タブ値を設定
            value={TAB_VALUE.CHANGE}
            // 依頼期間後の場合は無効化
            disabled={isAfterRequestTerm}
          />
        </TabList>
        {/** アンケートコンポーネント */}
        {/* // 質問タブのパネルを定義 */}
        <TabPanel value={TAB_VALUE.QUESTION}>
          {/* // 期間前なら注意表示、そうでなければフォームを表示 */}
          {isBeforeRequestTerm ? <OutsideSurveyPeriodTypography requestStatus={'before'} /> : <EnqueteAnswerGeneralQuestionCompliaFormTab />}
        </TabPanel>
        {/* // 質問(アクション⑤)タブのパネルを定義 */}
        <TabPanel value={TAB_VALUE.QUESTION_ACTION5}>
          {/* // 期間前なら注意表示、そうでなければフォームを表示 */}
          {isBeforeRequestTerm ? <OutsideSurveyPeriodTypography requestStatus={'before'} /> : <EnqueteAnswerGeneralQuestionCompliaFormAction5Tab />}
        </TabPanel>
        {/** 回答確認状況コンポーネント */}
        {/* // 確認状況タブのパネルを定義 */}
        <TabPanel value={TAB_VALUE.CONFIRM}>
          {/* // 期間前なら注意表示、そうでなければ確認タブを表示 */}
          {isBeforeRequestTerm ? <OutsideSurveyPeriodTypography requestStatus={'before'} /> : <EnqueteConfirmTab />}
        </TabPanel>
        {/* // 回答者変更タブのパネルを定義 */}
        <TabPanel value={TAB_VALUE.CHANGE}>
          {/* // 期間後なら注意表示 */}
          {isAfterRequestTerm ? (
            // 期間後の注意表示コンポーネント
            <OutsideSurveyPeriodTypography requestStatus={'after'} />
          ) : (
            // 期間内なら回答者変更コンポーネントを表示
            <ChangeRespondentComponent
              // 回答者リクエスト ID を渡す
              respondentRequestId={respondentRequestData.id}
              // 回答者ターゲット ID を渡す
              respondentTargetId={respondentTargetData.id}
              // 組織 ID を渡す
              organizationId={respondentTargetData.organizationId}
            />
          )}
        </TabPanel>
      </Paper>
    </TabContext>
  );
};
